import React, { useState } from "react"
import { graphql, navigate, useStaticQuery } from "gatsby"

import { SearchIcon } from "@heroicons/react/solid"
import { useFlexSearch } from "react-use-flexsearch"

export default function Search() {
	const data = useStaticQuery(graphql`
		query SearchQuery {
			localSearchSearch {
				index
				store
			}
			allStoryblokEntry(
				filter: { 
					field_component: { eq: "page" }
				}
			) {
				nodes {
					id
					name
					slug
					full_slug
				}
			}
		}
	`)

	// Filter out home page and pages starting with _
	const storyblokPages = data.allStoryblokEntry.nodes.filter(
		page => page.slug !== "home" && !page.slug?.startsWith("_")
	)
	
	const index = data.localSearchSearch.index
	const store = data.localSearchSearch.store
	const [query, setQuery] = useState("")

	// Use FlexSearch with options to ensure all matches are found
	const results = useFlexSearch(query, index, store, {
		limit: 100, // Increase limit to ensure all results are returned
		enrich: true, // Ensure full document is returned
	})

	const handleChange = e => {
		setQuery(e.target.value)
	}

	const handleSubmit = e => {
		e.preventDefault()
		
		if (!query.trim()) return
		
		// Get FlexSearch results (should already include Storyblok pages if indexed)
		const flexSearchResults = results || []
		
		// Get IDs of results already found by FlexSearch
		const foundIds = new Set(flexSearchResults.map(r => r.id))
		
		// Find Storyblok pages that match the search query (case-insensitive)
		// and are not already in FlexSearch results
		const queryLower = query.toLowerCase().trim()
		const matchingStoryblokPages = storyblokPages
			.filter(page => {
				// Check if page name or slug contains the search query
				const nameMatch = page.name?.toLowerCase().includes(queryLower)
				const slugMatch = page.slug?.toLowerCase().includes(queryLower)
				const fullSlugMatch = page.full_slug?.toLowerCase().includes(queryLower)
				
				// Only include if not already in FlexSearch results
				return (nameMatch || slugMatch || fullSlugMatch) && !foundIds.has(page.id)
			})
			.map(page => ({
				id: page.id,
				name: page.name,
				slug: page.slug,
				full_slug: page.full_slug,
				isStoryblokPage: true,
				permalink: page.full_slug,
				excerpt: null,
				from: null,
				authorizedOn: null,
				fileName: null,
				description: null,
				author: null,
				file: null,
				createdDate: null,
				contentVisibility: null,
				eventId: null,
				abstract: null,
			}))
		
		// Combine FlexSearch results with matching Storyblok pages
		const allResults = [...flexSearchResults, ...matchingStoryblokPages]
		
		// Sort results: Storyblok pages first, then by name match relevance
		const sortedResults = allResults.sort((a, b) => {
			// Prioritize Storyblok pages
			if (a.isStoryblokPage && !b.isStoryblokPage) return -1
			if (!a.isStoryblokPage && b.isStoryblokPage) return 1
			
			// Then prioritize exact/close name matches
			const aNameMatch = a.name?.toLowerCase().includes(queryLower)
			const bNameMatch = b.name?.toLowerCase().includes(queryLower)
			if (aNameMatch && !bNameMatch) return -1
			if (!aNameMatch && bNameMatch) return 1
			
			return 0
		})
		
		navigate("/search-results/", {
			state: { searchResults: sortedResults, search: query },
		})
	}

	return (
		<div className="flex justify-center">
			<div className="max-w-lg w-full lg:max-w-xs">
				<label htmlFor="search" className="sr-only">
					Search
				</label>
				<div className="relative text-white hover:text-primary focus-within:text-gray-400">
					<div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
						<SearchIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
					</div>
					<form onSubmit={handleSubmit} className="">
						<input
							type="text"
							name="searchValue"
							value={query}
							onChange={handleChange}
							className="block w-full bg-neutral py-2 pl-10 pr-3 border border-transparent rounded leading-5 placeholder-sky-100 focus:outline-none focus:border-white focus:placeholder-gray-500 focus:text-gray-900 focus:bg-white sm:text-sm"
						/>
					</form>
				</div>
			</div>
		</div>
	)
}
